import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { TIERS, ANNUAL_DISCOUNT_LABEL, fmtUsd, type Tier } from "@/lib/billing/tiers";
import { startCheckout, CheckoutNotConfiguredError } from "@/lib/billing/stripe";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useProfile } from "@/lib/profile/useProfile";
import PromoCodeInput from "@/components/billing/PromoCodeInput";
import CommunityAddOnUpsell from "@/components/billing/CommunityAddOnUpsell";
import { toast } from "sonner";
import { useEffect } from "react";

const Pricing = () => {
  const { session } = useAuth();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [annual, setAnnual] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState<string | null>(null);

  // Restore the tier the user was about to buy when they got bounced to /auth.
  const pendingTier = params.get("tier");
  const pendingInterval = params.get("interval") as "monthly" | "annual" | null;
  const refParam = params.get("ref"); // ?ref=WAVE-LUNA10 deep links from influencers

  useEffect(() => {
    if (params.get("checkout") === "cancel") {
      toast.info("Checkout canceled. Your card was not charged.");
    }
    if (pendingInterval === "annual") setAnnual(true);
    if (refParam && !promoCode) setPromoCode(refParam.toUpperCase());
    // Auto-resume checkout for signed-in users returning from /auth
    if (session && pendingTier) {
      const tier = TIERS.find((t) => t.id === pendingTier);
      if (tier && tier.id !== "tier_signal") {
        void handleSelect(tier);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const handleSelect = async (tier: Tier) => {
    if (tier.id === "tier_signal") {
      // Free tier = just create account
      if (!session) navigate("/auth?next=/account");
      else navigate("/account");
      return;
    }
    if (!session) {
      const interval = annual ? "annual" : "monthly";
      navigate(`/auth?next=${encodeURIComponent(`/pricing?tier=${tier.id}&interval=${interval}`)}`);
      return;
    }
    if (profile && !profile.onboarding_completed_at) {
      navigate("/onboarding");
      return;
    }
    setBusyId(tier.id);
    try {
      const { url } = await startCheckout({
        tierId: tier.id,
        interval: annual ? "annual" : "monthly",
        promoCode,
      });
      window.location.href = url;
    } catch (err) {
      if (err instanceof CheckoutNotConfiguredError) {
        toast.error(err.message);
      } else {
        toast.error(err instanceof Error ? err.message : "Could not start checkout.");
      }
      setBusyId(null);
    }
  };

  const cards = useMemo(() => TIERS, []);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <main className="flex-1 mx-auto w-full max-w-6xl px-6 py-16">
        <header className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">
            Pricing · 18+
          </p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight">
            Pick your frequency.
          </h1>
          <p className="mt-4 text-muted-foreground">
            Every paid plan starts with a 7-day free trial. Safety features —
            Crisis Resources, the Emergency Button, Incognito Mode — are free
            on every tier, including Signal. No exceptions.
          </p>
        </header>

        <div className="mt-10 flex items-center justify-center gap-3">
          <span className={`text-sm ${!annual ? "text-foreground font-medium" : "text-muted-foreground"}`}>
            Monthly
          </span>
          <Switch checked={annual} onCheckedChange={setAnnual} aria-label="Toggle annual pricing" />
          <span className={`text-sm ${annual ? "text-foreground font-medium" : "text-muted-foreground"}`}>
            Annual
          </span>
          <span className="hidden sm:inline-block text-xs text-success ml-2">
            {ANNUAL_DISCOUNT_LABEL}
          </span>
        </div>
        <p className="sm:hidden text-center mt-2 text-xs text-success">
          {ANNUAL_DISCOUNT_LABEL}
        </p>

        <div className="mt-6 flex justify-center">
          <div className="w-full max-w-sm">
            <PromoCodeInput onApplied={setPromoCode} context="checkout" attribute={!!session} />
          </div>
        </div>

        <section className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-5">
          {cards.map((tier) => {
            const isFree = tier.id === "tier_signal";
            const price = annual && tier.annualUsd != null
              ? tier.annualUsd
              : tier.monthlyUsd;
            const interval = isFree ? "" : annual ? "/yr" : "/mo";
            const isCurrent = profile?.tier === tier.id;
            const featured = tier.featured;

            return (
              <article
                key={tier.id}
                className={`relative rounded-2xl border p-6 flex flex-col bg-card transition-colors ${
                  featured
                    ? "border-foreground shadow-[0_0_0_1px_hsl(var(--foreground))]"
                    : "border-border"
                }`}
              >
                {featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-foreground text-background px-3 py-1 text-[10px] font-semibold uppercase tracking-wider">
                    Most popular
                  </span>
                )}
                <header>
                  <h2 className="text-lg font-semibold">{tier.name}</h2>
                  <p className="mt-1 text-xs text-muted-foreground min-h-[2.5em]">
                    {tier.tagline}
                  </p>
                </header>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-4xl font-bold tracking-tight">
                    {isFree ? "Free" : fmtUsd(price)}
                  </span>
                  {!isFree && (
                    <span className="text-sm text-muted-foreground">{interval}</span>
                  )}
                </div>
                {tier.trialDays > 0 && (
                  <p className="mt-1 text-xs text-success">
                    {tier.trialDays}-day free trial
                  </p>
                )}
                {tier.communities === 3 && (
                  <p className="mt-1 text-xs text-foreground/80">All 3 communities</p>
                )}

                <ul className="mt-6 space-y-2 text-sm flex-1">
                  {tier.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-foreground/60 shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSelect(tier)}
                  disabled={isCurrent || busyId === tier.id}
                  variant={featured ? "default" : "outline"}
                  className="mt-6 w-full"
                >
                  {isCurrent
                    ? "Your current plan"
                    : busyId === tier.id
                    ? "Starting…"
                    : isFree
                    ? "Start with Signal"
                    : "Start free trial"}
                </Button>
              </article>
            );
          })}
        </section>

        <div className="mt-12 border-t border-border pt-8">
          <CommunityAddOnUpsell />
        </div>

        <footer className="mt-16 max-w-2xl mx-auto text-center text-xs text-muted-foreground space-y-2">
          <p>
            Cancel anytime from your account. Annual plans are billed once per year.
            Trial converts to a paid subscription at the end of the 7-day window
            unless canceled.
          </p>
          <p>
            Looking for one-off boosts and add-ons?{" "}
            <Link to="/a-la-carte" className="underline hover:text-foreground">
              Browse the à la carte menu
            </Link>
            .
          </p>
          <p>
            Questions? Read our{" "}
            <Link to="/terms" className="underline hover:text-foreground">Terms</Link> and{" "}
            <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
          </p>
        </footer>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Pricing;
